import { useState } from 'react';

import { useAddToast } from '../../../contexts/ToastContext';
import OrgSelectForm from '../components/OrgSelectForm';
import { presentRequestError } from '../errorPresentation';
import { useLoginResponseEffect } from '../hooks/useLoginResponseEffect';
import { useSocialComplete } from '../hooks/useSocialComplete';
import { STRATEGY_LABEL } from '../strategyLabels';
import type { MfaMethodType } from '../../../../shared/model-typings/mfaMethod';
import type { Candidate, OAuthProvider } from '../../../../shared/types/routes/auth/v2';
import type { OrgOption } from '../components/OrgSelectForm';

export type SocialOrgPickerStepProps = {
  candidates: Candidate[];
  /** The provider the user authenticated with — drives which orgs are selectable vs disabled. */
  provider: OAuthProvider;
  /** The org id an OIDC client hinted at (`?org_hint=`), preselected when selectable with this provider. */
  preferredOrgId?: number | null;
  onMfaRequired: (mfaMethod: MfaMethodType) => void;
  onBack: () => void;
};

/**
 * The pending-social org picker: the user signed in with Google against an identity that matches members of more than
 * one org, so they choose which org to complete the login into. Renders the shared {@link OrgSelectForm} and drives
 * `POST /api/auth/login/social/complete`. Like the credential path, MFA can gate the finalize — an `mfa_required`
 * result hands off to the shared MFA step.
 */
export const SocialOrgPickerStep = (
  { candidates, provider, preferredOrgId, onMfaRequired, onBack }: SocialOrgPickerStepProps,
) => {
  const [error, setError] = useState<string | null>(null);
  const socialComplete = useSocialComplete();
  const addToast = useAddToast();

  // On success, hand off to the MFA step or redirect into the app. (The error path stays inline below since it
  // doesn't unmount the step.)
  useLoginResponseEffect(socialComplete.data, onMfaRequired);

  // An org is selectable only when the provider the user signed in with is among its allowed strategies;
  // otherwise it's shown disabled with the methods it IS reachable by (e.g. "Only available via SAML").
  // Selectable orgs come first (so the default selection/focus lands on a usable one), disabled orgs last,
  // each group sorted by name.
  const options: OrgOption[] = candidates
    .map(candidate => {
      const isDisabled = !candidate.allowedStrategies.includes(provider);
      return {
        organizationId: candidate.organizationId,
        label: candidate.organizationName,
        isDisabled,
        secondaryLabel: isDisabled
          ? `Only available via ${candidate.allowedStrategies.map(strategy => STRATEGY_LABEL[strategy]).join(' or ')}`
          : undefined,
      };
    })
    // `Number(false)` is 0 and `Number(true)` is 1, so coercing `isDisabled` makes selectable orgs (0) sort
    // ahead of disabled ones (1). When both sides are in the same group the difference is 0, so `||` falls
    // through to the alphabetical name comparison.
    .sort((a, b) => Number(a.isDisabled) - Number(b.isDisabled) || a.label.localeCompare(b.label));

  const providerLabel = STRATEGY_LABEL[provider];
  const legend = options.some(option => !option.isDisabled)
    ? `Choose which organization to sign in to with ${providerLabel}:`
    : `Your ${providerLabel} account can't be used to sign in to these organizations:`;

  const handleSubmit = (organizationId: number) => {
    if (socialComplete.isLoading) {
      return;
    }
    setError(null);
    socialComplete.mutate(
      { organizationId },
      {
        onError: requestError => {
          // An expired/invalid pending login (400) is an expected outcome shown inline; anything else (network
          // failure, 5xx) is unexpected and surfaces as a toast.
          const presented = presentRequestError(requestError, {
            inlineFallback: 'Could not complete sign-in. Please try again.',
            toastFallback: 'Something went wrong. Please try again.',
          });
          if (presented.kind === 'inline') {
            setError(presented.message);
            return;
          }
          addToast({ type: 'error', content: presented.message });
        },
      },
    );
  };

  return (
    <OrgSelectForm
      legend={legend}
      options={options}
      preferredOrgId={preferredOrgId}
      onSubmit={handleSubmit}
      onBack={onBack}
      isSubmitting={socialComplete.isLoading}
      errorMessage={error}
    />
  );
};
